import { useRef, useState, type JSX, type KeyboardEvent } from "react";
import { useTranslation } from "react-i18next";

import { useCan } from "@/shared/auth";

import { DeliveryLogPanel } from "../components/DeliveryLogPanel";
import { SettingsPanel } from "../components/SettingsPanel";

import { NotificationsInboxPage } from "./NotificationsInboxPage";

export type NotifTab = "inbox" | "settings" | "delivery";

interface NotificationsPageProps {
  initialTab?: NotifTab | undefined;
}

export function NotificationsPage({ initialTab }: NotificationsPageProps): JSX.Element {
  const { t } = useTranslation("notifications");
  const canSettings = useCan("notifications:settings");
  const canDeliveries = useCan("notifications:deliveries");

  const tabs: Array<{ key: NotifTab; label: string }> = [
    { key: "inbox", label: t("tabs.inbox") },
    ...(canSettings ? [{ key: "settings" as NotifTab, label: t("tabs.settings") }] : []),
    ...(canDeliveries ? [{ key: "delivery" as NotifTab, label: t("delivery.tab") }] : []),
  ];

  const visibleKeys = tabs.map((tab) => tab.key);

  // A requested tab is effective only if it's currently in the visible tabs list.
  function resolveTab(requested: NotifTab | undefined): NotifTab {
    if (requested !== undefined && visibleKeys.includes(requested)) return requested;
    return "inbox";
  }

  const [activeTab, setActiveTab] = useState<NotifTab>(() => resolveTab(initialTab));

  // M-2: Derived effective tab — auto-fall-back to inbox if perm is revoked mid-session.
  const effectiveTab: NotifTab = visibleKeys.includes(activeTab) ? activeTab : "inbox";

  // M-1: Roving tabindex — refs map for focus management.
  const tabRefs = useRef<Map<NotifTab, HTMLButtonElement | null>>(new Map());

  function setTabRef(key: NotifTab) {
    return (el: HTMLButtonElement | null) => {
      tabRefs.current.set(key, el);
    };
  }

  // onKeyDown is placed on each <button> (inherently focusable) to satisfy
  // jsx-a11y/interactive-supports-focus; tablist div needs no tabIndex.
  function handleTabKeyDown(event: KeyboardEvent<HTMLButtonElement>) {
    if (visibleKeys.length <= 1) return; // no-op when only one tab is visible

    const currentIndex = visibleKeys.indexOf(effectiveTab);
    let nextIndex: number | null = null;

    if (event.key === "ArrowRight") {
      nextIndex = (currentIndex + 1) % visibleKeys.length;
    } else if (event.key === "ArrowLeft") {
      nextIndex = (currentIndex - 1 + visibleKeys.length) % visibleKeys.length;
    } else if (event.key === "Home") {
      nextIndex = 0;
    } else if (event.key === "End") {
      nextIndex = visibleKeys.length - 1;
    }

    if (nextIndex !== null) {
      event.preventDefault();
      const nextKey = visibleKeys[nextIndex];
      if (nextKey !== undefined) {
        setActiveTab(nextKey);
        tabRefs.current.get(nextKey)?.focus();
      }
    }
  }

  return (
    <div>
      {/* I-1: tablist label uses tabs.label, not a tab's own name */}
      <div
        role="tablist"
        aria-label={t("tabs.label")}
        className="mb-4 flex gap-1 border-b border-[var(--premium-border)]"
      >
        {tabs.map((tab) => (
          <button
            key={tab.key}
            ref={setTabRef(tab.key)}
            type="button"
            role="tab"
            id={`notif-tabbtn-${tab.key}`}
            aria-selected={effectiveTab === tab.key}
            aria-controls={`notif-tabpanel-${tab.key}`}
            tabIndex={effectiveTab === tab.key ? 0 : -1}
            data-testid={`notif-tab-${tab.key}`}
            onClick={() => setActiveTab(tab.key)}
            onKeyDown={handleTabKeyDown}
            className={[
              "px-4 py-2 text-sm font-medium transition-colors",
              "border-b-2 -mb-px",
              effectiveTab === tab.key
                ? "border-[var(--intent-primary-fg)] text-[var(--text-primary)]"
                : "border-transparent text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:border-[var(--premium-border-strong)]",
            ].join(" ")}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* I-2: tabpanel wraps active content; keeps notifications-tab-host testid */}
      <div
        role="tabpanel"
        id={`notif-tabpanel-${effectiveTab}`}
        aria-labelledby={`notif-tabbtn-${effectiveTab}`}
        data-testid="notifications-tab-host"
      >
        {effectiveTab === "inbox" && <NotificationsInboxPage />}
        {effectiveTab === "settings" && canSettings && <SettingsPanel />}
        {effectiveTab === "delivery" && canDeliveries && <DeliveryLogPanel />}
      </div>
    </div>
  );
}
